import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from "@angular/common/http";
import {firstValueFrom} from "rxjs";
import {environment} from "../../environments/environment";
import {v4 as uuidv4} from 'uuid';
import {ToastService} from "../core/notifications/toast-service";

@Injectable({
  providedIn: 'root'
})
export class ImageService {

  constructor(private httpClient: HttpClient,
              private toastService: ToastService) {
  }

  async getBanner(): Promise<string> {
    const headers = new HttpHeaders({
      'Accept': 'application/octet-stream',
      'Content-Type': 'application/octet-stream'
    });

    const params = new HttpParams().set('key', "banner-image");
    const blob = await firstValueFrom(
      this.httpClient.get(environment.apiUrl + "upload/image", {
        headers: headers,
        params: params,
        responseType: "blob"
      })
    )

    return URL.createObjectURL(blob);
  }

  async getImage(imageURL: string): Promise<string> {
    const headers = new HttpHeaders({
      'Accept': 'application/octet-stream',
      'Content-Type': 'application/octet-stream'
    });

    const params = new HttpParams().set('key', imageURL);
    const blob = await firstValueFrom(
      this.httpClient.get(environment.apiUrl + "upload/image", {
        headers: headers,
        params: params,
        responseType: "blob"
      })
    )

    return URL.createObjectURL(blob);
  }

  async uploadImage(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.readAsArrayBuffer(file);
      reader.onload = async () => {
        try {
          // When file is loaded, get the ArrayBuffer and send the PUT request
          const arrayBuffer = reader.result;
          const blob = new Blob([new Uint8Array(arrayBuffer as ArrayBuffer)], {type: 'application/octet-stream'});

          const headers = new HttpHeaders({
            'Accept': 'application/json',
            'Content-Type': 'application/octet-stream'
          });

          const imgUuid = uuidv4(); // Generate a UUID for the image
          const params = new HttpParams().set('key', imgUuid);

          await firstValueFrom(this.httpClient.put(environment.apiUrl + "upload/image", blob, {
            headers: headers,
            params: params
          }));

          this.toastService.showSuccess("Bild erfolgreich hochgeladen");
          resolve(imgUuid); // Resolve the promise with imgUuid on success
        } catch (error) {
          this.toastService.showDanger("Bild konnte nicht hochgeladen werden");
          reject(error); // Reject the promise on error
        }
      };

      reader.onerror = (error) => {
        this.toastService.showDanger("Fehler beim Lesen des Bildes");
        reject(error); // Reject the promise on FileReader error
      };
    });
  }

  async uploadStandardImage(file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.readAsArrayBuffer(file);
      reader.onload = async () => {
        try {
          const arrayBuffer = reader.result;
          const blob = new Blob([new Uint8Array(arrayBuffer as ArrayBuffer)], { type: 'application/octet-stream' });

          const headers = new HttpHeaders({
            'Accept': 'application/json',
            'Content-Type': 'application/octet-stream'
          });

          // Statt UUID: fester Schlüssel
          const standardKey = "banner-image";
          const params = new HttpParams().set('key', standardKey);

          await firstValueFrom(this.httpClient.put(environment.apiUrl + "upload/image", blob, {
            headers: headers,
            params: params
          }));

          this.toastService.showSuccess("Banner erfolgreich hochgeladen");
          resolve(); // Kein Rückgabewert nötig, außer du willst den Key trotzdem zurückgeben
        } catch (error) {
          this.toastService.showDanger("Banner konnte nicht hochgeladen werden");
          reject(error);
        }
      };

      reader.onerror = (error) => {
        this.toastService.showDanger("Fehler beim Lesen des Banners");
        reject(error);
      };
    });
  }
}

